import { Deserializable } from '../../../protocols/deserializable';
import { HighGuideLeadingAxis } from './high-guide-leading-axis';
import { ScoreTrendDirection } from './score-trend-direction';

/**
 * One side of a `HighGuideProductSignalSnapshot` - the same shape is used for the company rollup and the global
 * (network wide) rollup, so whichever one you are holding, read it the same way.
 *
 * Field-for-field port of the model in the HighGuide app, which is the only thing that renders it. The dashboard
 * holds it purely to relay the websocket-pushed company rollup into the iframe.
 *
 * Every number here is precomputed and dated by the backend as of `snapshotDate`, and none of it is derivable from
 * the signals the client holds. Recomputing any of it locally would silently disagree with the backend, so treat
 * the whole object as read-only display data.
 */
export class HighGuideSignalSnapshotSummary implements Deserializable {
  /** `YYYY-MM-DD`, not a timestamp. Snapshots are recomputed on a daily cadence. */
  public snapshotDate: string;

  public signalCount: number;
  public noteCount: number;
  public favoriteCount: number;
  public boostCount: number;
  public likeCount: number;
  public passCount: number;
  public contributorCount: number;

  public productSignalScore: number;
  public netSignalScore: number;
  public avgOverallFeel: number;
  public avgDankness: number;
  public avgValueForMoney: number;

  /**
   * Keyed and typed as raw strings rather than `HighGuideRecommendationTag` on purpose. The backend tag vocabulary
   * and the union in `high-guide-recommendation-tag.ts` are currently out of step - the service emits values such
   * as `PriorityStock` that the union does not contain, and spells others differently (`ToldMyFriends` vs
   * `ToldAFriend`). Typing these as the union would be a lie the compiler happily accepts.
   *
   * `StringifyUtils.highGuideAppReplacer` flattens this `Map` back into a plain object on the way out to the
   * iframe, which is exactly what the receiving deserializer expects.
   */
  public tagCounts: Map<string, number>;

  /** Same caveat as `tagCounts`. Ordered by the backend; do not re-sort. */
  public topTags: string[];

  /** `null` when no axis scored high enough to call out. See `HighGuideLeadingAxis`. */
  public leadingAxis: HighGuideLeadingAxis;

  public trendScore: number;
  public trendDirection: ScoreTrendDirection;

  /** LLM generated from the notes behind this snapshot. Any of these may be empty. */
  public summary: string;
  public keywords: string[];
  public themes: string[];
  public concerns: string[];

  onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    this.tagCounts = Deserialize?.genericMap(this.tagCounts) ?? new Map<string, number>();
    this.topTags = this.topTags ?? [];
    this.leadingAxis = Deserialize?.instanceOf(HighGuideLeadingAxis, this.leadingAxis);
    this.keywords = this.keywords ?? [];
    this.themes = this.themes ?? [];
    this.concerns = this.concerns ?? [];
  }
}
